import { useState, useEffect } from "react";
import { Moon, Sun, Monitor, Check, PanelLeft, PanelLeftClose, Info } from "lucide-react";
import { useTheme } from "../../components/themeProvider";
import { Button } from "../../components/ui/button";
import { Select } from "antd";

const Appearance = () => {
    const { theme, setTheme, mounted } = useTheme();
    const [selectedTheme, setSelectedTheme] = useState(theme);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Theme options for Antd Select
    const themeOptions = [
        {
            value: 'light',
            label: (
                <div className="flex items-center gap-3 py-1">
                    <Sun className="h-4 w-4 text-amber-500" />
                    <span>Light Mode</span>
                </div>
            )
        },
        {
            value: 'dark',
            label: (
                <div className="flex items-center gap-3 py-1">
                    <Moon className="h-4 w-4 text-blue-500" />
                    <span>Dark Mode</span>
                </div>
            )
        },
        {
            value: 'system',
            label: (
                <div className="flex items-center gap-3 py-1">
                    <Monitor className="h-4 w-4 text-gray-500" />
                    <span>System Default</span>
                </div>
            )
        }
    ];

    // Sidebar options for Antd Select
    const sidebarOptions = [
        {
            value: 'expanded',
            label: (
                <div className="flex items-center gap-3 py-1">
                    <PanelLeft className="h-4 w-4 text-green-500" />
                    <span>Expanded Sidebar</span>
                </div>
            )
        },
        {
            value: 'collapsed',
            label: (
                <div className="flex items-center gap-3 py-1">
                    <PanelLeftClose className="h-4 w-4 text-blue-500" />
                    <span>Collapsed Sidebar</span>
                </div>
            )
        }
    ];

    // Load sidebar preference from localStorage on mount
    useEffect(() => {
        if (mounted) {
            const storedSidebarState = localStorage.getItem('sidebar_preference');
            if (storedSidebarState) {
                const isCollapsed = storedSidebarState === 'collapsed';
                setSidebarCollapsed(isCollapsed);
                applySidebarPreference(isCollapsed);
            }

            // Load theme preferences
            const storedTheme = localStorage.getItem('theme-preference');
            if (storedTheme === 'dark') {
                setTheme('dark');
                setSelectedTheme('dark');
                applyThemeToDocument('dark');
            } else {
                setSelectedTheme(theme);
            }
        }
    }, [mounted, theme, setTheme]);

    // Handle theme change
    const handleThemeChange = (newTheme) => {
        setSelectedTheme(newTheme);
        setTheme(newTheme);

        // Store in localStorage
        localStorage.setItem('theme', newTheme);

        // Force immediate theme application
        applyThemeToDocument(newTheme);
    };

    // Handle sidebar preference change
    const handleSidebarChange = (newState) => {
        const isCollapsed = newState === 'collapsed';
        setSidebarCollapsed(isCollapsed);

        // Store in localStorage only
        localStorage.setItem('sidebar_preference', newState);

        // Apply sidebar preference
        applySidebarPreference(isCollapsed);

        // Dispatch custom event for other components to listen to
        window.dispatchEvent(new CustomEvent('sidebarPreferenceChange', {
            detail: { collapsed: isCollapsed }
        }));
    };

    // Apply theme class to document element
    const applyThemeToDocument = (themeToApply) => {
        const root = window.document.documentElement;

        root.classList.remove('light', 'dark');

        if (themeToApply === 'system') {
            const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            root.classList.add(systemTheme);
        } else {
            root.classList.add(themeToApply);
        }
    };

    // Apply sidebar preference
    const applySidebarPreference = (isCollapsed) => {
        const root = window.document.documentElement;

        // Remove existing classes
        root.classList.remove('sidebar-expanded', 'sidebar-collapsed');

        // Add new class
        if (isCollapsed) {
            root.classList.add('sidebar-collapsed');
        } else {
            root.classList.add('sidebar-expanded');
        }

        // Also update data attribute for CSS targeting
        root.setAttribute('data-sidebar', isCollapsed ? 'collapsed' : 'expanded');
    };

    // Toggle sidebar state
    const toggleSidebar = () => {
        const newState = !sidebarCollapsed;
        handleSidebarChange(newState ? 'collapsed' : 'expanded');
    };

    if (!mounted) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-6"></div>
                    <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
                    <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-64"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-0">
            {/* Header */}
            <div className="mb-8">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Appearance & Layout
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                    Customize how the application looks and behaves on your device
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Theme Selection Card */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">
                            Theme Preferences
                        </h2>

                        {/* Theme Selector */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    Choose Theme
                                </label>
                                <Select
                                    value={selectedTheme}
                                    onChange={handleThemeChange}
                                    options={themeOptions}
                                    className="w-full"
                                    size="large"
                                />
                            </div>

                            {/* Quick Theme Toggle */}
                            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    Quick Toggle
                                </label>
                                <Button
                                    onClick={() => handleThemeChange(theme === 'dark' ? 'light' : 'dark')}
                                    variant="outline"
                                    className="w-full flex items-center justify-center gap-3 py-3 h-auto"
                                >
                                    {theme === 'dark' ? (
                                        <>
                                            <Sun className="h-5 w-5 text-amber-500" />
                                            <span>Switch to Light Mode</span>
                                        </>
                                    ) : (
                                        <>
                                            <Moon className="h-5 w-5 text-blue-500" />
                                            <span>Switch to Dark Mode</span>
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sidebar Preferences Card */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        <h2 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-3">
                            Sidebar Preferences
                        </h2>

                        {/* Sidebar State Selector */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    Sidebar State
                                </label>
                                <Select
                                    value={sidebarCollapsed ? 'collapsed' : 'expanded'}
                                    onChange={handleSidebarChange}
                                    options={sidebarOptions}
                                    className="w-full"
                                    size="large"
                                />
                            </div>

                            {/* Quick Sidebar Toggle */}
                            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-2 mb-3">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                        Keyboard Shortcut
                                    </label>
                                    <div className="group relative">
                                        <Info className="h-4 w-4 text-gray-400 cursor-help" />
                                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                            Works anywhere in the app
                                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-3 rounded-lg border">
                                    Use <kbd className="px-1.5 py-0.5 text-xs border rounded bg-white dark:bg-gray-700 font-mono">Ctrl + B</kbd> to toggle sidebar
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Guidelines Card */}
            <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    Layout Guidelines
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Theme Guidelines */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                            Theme Options
                        </h4>
                        <div className="space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mt-0.5">
                                    <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        Light Mode
                                    </h4>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        Best for well-lit environments and daytime use
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mt-0.5">
                                    <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        Dark Mode
                                    </h4>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        Reduces eye strain in low-light conditions
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mt-0.5">
                                    <Check className="h-3 w-3 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        System Default
                                    </h4>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        Automatically follows your device's theme setting
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sidebar Guidelines */}
                    <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                            Sidebar Options
                        </h4>
                        <div className="space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mt-0.5">
                                    <Check className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        Expanded Sidebar
                                    </h4>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        Shows full menu labels and icons
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mt-0.5">
                                    <Check className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        Collapsed Sidebar
                                    </h4>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        Compact view with icons only
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Information */}
            <div className="mt-8 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    💡 Pro Tips
                </h3>
                <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                    <li>• Your theme and sidebar preferences are saved automatically</li>
                    <li>• Use "System Default" to follow your OS theme</li>
                    <li>• Dark mode can help save battery on OLED screens</li>
                    <li>• Collapsed sidebar gives you more horizontal space</li>
                    <li>• Changes take effect immediately across the app</li>
                </ul>
            </div>
        </div>
    );
};

export default Appearance;